import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { GlassCard } from '../components/layout/GlassCard';
import { 
  Sparkles, 
  Video, 
  Instagram, 
  Youtube, 
  Hash, 
  TrendingUp, 
  Wand2,
  Loader2,
  Copy,
  Check,
  RefreshCw,
  Zap,
  FileText,
  Lightbulb
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOOLS = [
  {
    id: 'viral-script',
    name: 'Viral Script Generator',
    description: 'Create scroll-stopping reel scripts',
    icon: Video,
    color: 'from-pink-500 to-rose-500'
  },
  {
    id: 'caption',
    name: 'Caption Generator',
    description: 'Engaging captions with hashtags',
    icon: FileText,
    color: 'from-purple-500 to-indigo-500'
  },
  {
    id: 'youtube',
    name: 'YouTube Ideas',
    description: 'Titles, thumbnails & video ideas',
    icon: Youtube,
    color: 'from-red-500 to-orange-500'
  },
  {
    id: 'trending',
    name: 'Trending Topics',
    description: 'Discover what\'s hot in your niche',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'hashtags',
    name: 'Hashtag Generator',
    description: 'Trending hashtags for reach',
    icon: Hash,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'rewrite',
    name: 'Viral Rewriter',
    description: 'Transform content to go viral',
    icon: Wand2,
    color: 'from-amber-500 to-yellow-500'
  }
];

const PLATFORMS = ['instagram', 'tiktok', 'youtube_shorts'];
const STYLES = ['engaging', 'educational', 'funny', 'motivational', 'professional'];
const TONES = ['engaging', 'professional', 'casual', 'funny', 'inspirational'];

export default function CreatorModePage() {
  const { token } = useAuth();
  const [activeTool, setActiveTool] = useState('viral-script');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Form states
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [duration, setDuration] = useState(60);
  const [style, setStyle] = useState('engaging');
  const [tone, setTone] = useState('engaging');
  const [niche, setNiche] = useState('');
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('video');
  const [ideaCount, setIdeaCount] = useState(5);

  const handleGenerate = async () => {
    setLoading(true);
    setResult(null);

    try {
      let endpoint = '';
      let payload = {};

      switch (activeTool) {
        case 'viral-script':
          if (!topic) {
            toast.error('Please enter a topic');
            setLoading(false);
            return;
          }
          endpoint = '/api/creator/viral-script';
          payload = { topic, platform, duration, style };
          break;

        case 'caption':
          if (!content) {
            toast.error('Please describe your content');
            setLoading(false);
            return;
          }
          endpoint = '/api/creator/caption';
          payload = { 
            content_description: content, 
            platform, 
            tone,
            include_cta: true,
            include_hashtags: true,
            hashtag_count: 15
          };
          break;

        case 'youtube':
          if (!niche) {
            toast.error('Please enter your niche');
            setLoading(false);
            return;
          }
          endpoint = '/api/creator/youtube-ideas';
          payload = { niche, content_type: contentType, count: ideaCount };
          break;

        case 'trending':
          if (!niche) {
            toast.error('Please enter your niche');
            setLoading(false);
            return;
          }
          endpoint = '/api/creator/trending';
          payload = { niche, platform };
          break;

        case 'hashtags':
          if (!content) {
            toast.error('Please describe your content');
            setLoading(false);
            return;
          }
          endpoint = '/api/creator/hashtags';
          payload = { content, count: 20 };
          break;

        case 'rewrite':
          if (!content) {
            toast.error('Please enter content to rewrite');
            setLoading(false);
            return;
          }
          endpoint = '/api/creator/viral-rewrite';
          payload = { original_content: content, platform };
          break;

        default:
          return;
      }

      const response = await axios.post(
        `${API_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      setResult(response.data);
      toast.success('Generated successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const renderToolForm = () => {
    switch (activeTool) {
      case 'viral-script':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Topic / Idea</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="input-field w-full"
                placeholder="e.g., 5 productivity hacks for entrepreneurs"
                data-testid="topic-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Platform</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="input-field w-full"
                >
                  {PLATFORMS.map(p => (
                    <option key={p} value={p} className="bg-gray-900">
                      {p.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Duration (sec)</label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
                  className="input-field w-full"
                  min="15"
                  max="180"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Style</label>
              <div className="flex flex-wrap gap-2">
                {STYLES.map(s => (
                  <button
                    key={s}
                    onClick={() => setStyle(s)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      style === s 
                        ? 'bg-primary text-white' 
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'caption':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Describe your post/reel</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="input-field w-full h-32 resize-none"
                placeholder="e.g., A reel showing my morning routine as a startup founder..."
                data-testid="content-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Platform</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="input-field w-full"
                >
                  {PLATFORMS.map(p => (
                    <option key={p} value={p} className="bg-gray-900">
                      {p.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="input-field w-full"
                >
                  {TONES.map(t => (
                    <option key={t} value={t} className="bg-gray-900">
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 'youtube':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Your Niche</label>
              <input
                type="text"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                className="input-field w-full"
                placeholder="e.g., Personal finance, Tech reviews, Fitness..."
                data-testid="niche-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Content Type</label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="input-field w-full"
                >
                  <option value="video" className="bg-gray-900">Long-form Video</option>
                  <option value="shorts" className="bg-gray-900">YouTube Shorts</option>
                  <option value="livestream" className="bg-gray-900">Livestream</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Number of Ideas</label>
                <input
                  type="number"
                  value={ideaCount}
                  onChange={(e) => setIdeaCount(parseInt(e.target.value) || 5)}
                  className="input-field w-full"
                  min="1"
                  max="10"
                />
              </div>
            </div>
          </div>
        );

      case 'trending':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Your Niche</label>
              <input
                type="text"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                className="input-field w-full"
                placeholder="e.g., Tech, Fashion, Food, Gaming..."
                data-testid="niche-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Platform Focus</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="input-field w-full"
              >
                <option value="all" className="bg-gray-900">All Platforms</option>
                {PLATFORMS.map(p => (
                  <option key={p} value={p} className="bg-gray-900">
                    {p.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'hashtags':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Describe your content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="input-field w-full h-24 resize-none"
                placeholder="e.g., A fitness transformation post, motivational gym content..."
                data-testid="content-input"
              />
            </div>
          </div>
        );

      case 'rewrite':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Your Original Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="input-field w-full h-32 resize-none"
                placeholder="Paste your caption, script, or content here..."
                data-testid="content-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Target Platform</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="input-field w-full"
              >
                {PLATFORMS.map(p => (
                  <option key={p} value={p} className="bg-gray-900">
                    {p.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderResult = () => {
    if (!result) return null;

    let displayContent = '';
    let hashtags = [];

    if (result.script) displayContent = result.script;
    else if (result.caption) displayContent = result.caption;
    else if (result.ideas) displayContent = result.ideas;
    else if (result.trends) displayContent = result.trends;
    else if (result.viral_version) displayContent = result.viral_version;

    if (result.hashtags) hashtags = result.hashtags;
    if (result.hashtag_string) hashtags = result.hashtag_string.split(' ');

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6"
      >
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Generated Content
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => copyToClipboard(displayContent)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-gray-400" />}
              </button>
              <button
                onClick={handleGenerate}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Regenerate"
              >
                <RefreshCw className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <pre className="whitespace-pre-wrap text-gray-300 text-sm bg-black/30 p-4 rounded-lg overflow-auto max-h-96">
              {displayContent}
            </pre>
          </div>

          {hashtags.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-400">Hashtags</h4>
                <button
                  onClick={() => copyToClipboard(hashtags.join(' '))}
                  className="text-xs text-primary hover:underline"
                >
                  Copy all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-primary/20 text-primary rounded-full text-sm cursor-pointer hover:bg-primary/30 transition-colors"
                    onClick={() => copyToClipboard(tag)}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.viral_hook_inspiration && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Lightbulb className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-400">Hook Inspiration</span>
              </div>
              <p className="text-sm text-gray-300">{result.viral_hook_inspiration}</p>
            </div>
          )}
        </GlassCard>
      </motion.div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="creator-mode-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-full border border-pink-500/30 mb-4">
          <Zap className="w-4 h-4 text-pink-400" />
          <span className="text-sm text-pink-300">Creator Mode</span>
        </div>
        <h1 className="font-heading text-4xl font-bold mb-2">Content Creator Toolkit</h1>
        <p className="text-gray-400">Generate viral content, captions, and ideas powered by AI</p>
      </div>

      {/* Tool Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {TOOLS.map((tool) => (
          <button
            key={tool.id}
            onClick={() => {
              setActiveTool(tool.id);
              setResult(null);
            }}
            className={`p-4 rounded-xl border transition-all text-left ${
              activeTool === tool.id
                ? 'border-primary bg-primary/10'
                : 'border-white/10 bg-white/5 hover:bg-white/10'
            }`}
            data-testid={`tool-${tool.id}`}
          >
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center mb-3`}>
              <tool.icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-medium text-white text-sm">{tool.name}</h3>
            <p className="text-xs text-gray-500 mt-1">{tool.description}</p>
          </button>
        ))}
      </div>

      {/* Tool Form */}
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-6">
          {TOOLS.find(t => t.id === activeTool) && (
            <>
              {React.createElement(TOOLS.find(t => t.id === activeTool).icon, {
                className: 'w-6 h-6 text-primary'
              })}
              <h2 className="text-xl font-semibold text-white">
                {TOOLS.find(t => t.id === activeTool).name}
              </h2>
            </>
          )}
        </div>

        {renderToolForm()}

        <button
          onClick={handleGenerate}
          disabled={loading}
          className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
          data-testid="generate-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate
            </>
          )}
        </button>
      </GlassCard>

      {/* Result */}
      <AnimatePresence>
        {renderResult()}
      </AnimatePresence>
    </div>
  );
}
